import { _ } from "../../core/polyglot/polyglot.js";
import accountingDocument from "../shared/accountingDocument.js";
import accountRowVisibilityFactory from "../../models/reports/accountRowVisibilities/accountRowVisibilityFactory.js";
import AlertCollector from "../../core/alerts/AlertCollector.js";
import columnsSettingsWidget from "./columnsSettingsWidget.js";
import ComparingPeriodDropdownWidget from "../reports/ComparingPeriodDropdownWidget.js";
import DisclaimerIconWidget from "../../core/widgets/DisclaimerIconWidget/DisclaimerIconWidget.js";
import displaySettingsWidget from "./displaySettingsWidget.js";
import InlineDimensionLabelsWidget from "../../core/widgets/InlineDimensionLabelsWidget.js";
import makeReportExporterPicker from "./export/makeReportExporterPicker.js";
import multipleDimensionsPickerWidget from "../../core/widgets/multipleDimensionsPickerWidget.js";
import optionsBag from "../../models/options/optionsBag.js";
import popoverActionButtonWidget from "../../core/widgets/popoverActionButtonWidget.js";
import ReportDisclaimer from "../../models/ReportDisclaimer.js";
import ReportDisclaimerWidget from "../../core/widgets/ReportDisclaimerWidget.js";
import ReportService from "./ReportService.js";
import ReportViewerWidget from "./ReportViewerWidget/ReportViewerWidget.js";
import rowsSettingsWidget from "./rowsSettingsWidget.js";
import session from "../../core/session.js";
import SwitchButton from "../../core/widgets/SwitchButton/SwitchButton.js";
import theme from "../../core/theme.js";
import ValidationResultWidget from "../../core/widgets/ValidationResultWidget.js";

/**
 * Document used to display a report.
 */
const reportViewerDocument = accountingDocument.subclass((that, my) => {
	my.initialize = function () {
		my.super(...arguments);

		my.keyFigures = null;

		/**
		 * Map report item templates to their settings (such as dimensions
		 * and budgets) to be used in instantiating contexts.
		 */
		my.itemsSettings = new Map();

		that.documentId = "report-viewer";

		that.title = (html) => {
			if (my.getReport()) {
				html.render(
					my.getReport().getTitle(),
					optionsBag.fromId("disclaimer").renderWhenActive(
						new DisclaimerIconWidget({
							company: session.getCompany(),
							period: session.getPeriod(),
							agency: session.agency,
						}),
					),
				);
			} else {
				html.render(_("Report viewer"));
			}
		};

		that.subtitle = (html) => {
			if (!my.getReport()) {
				return;
			}
			html.div({ klass: "company-name" }, session.getCompany().getName());
		};

		that.help = function () {
			return [
				{
					title: _("Periods"),
					content: _(
						"You can view this report for all possible periods.",
					),
					element: "period-picker",
				},

				{
					title: _("Filter by project"),
					content: _(
						"By default, report reflects the entire business. But you can limit it to only reflect the dimension you pick; such as cost places or projects.",
					),
					element: "dimensions-picker",
				},
				{
					title: _("Display settings"),
					content: _(
						"Your administrator picks the defaults for you but you can alter it for the current session.",
					),
					element: "display-settings",
				},
				{
					title: _("Exporting"),
					content: _(
						"You can create a PDF of this report or get a copy of it as an Excel spreadsheet or CSV file.",
					),
					element: "export-button",
				},
				{
					title: _("Printing"),
					content: _(
						"Please use the latest version of your browser for the optimal printing experience.",
					),
					element: "print-button",
				},
			];
		};

		my.reportService = new ReportService({
			company: session.getCompany(),
			period: session.getPeriod(),
			chartOfAccountsId: session.getCurrentChartOfAccountsId(),
		});

		session.onPeriodChange((period) => {
			my.reportService.setPeriod(period);
		});

		my.validationResultCollector = new AlertCollector();

		that.showEmpty();
	};

	my.initializeSubwidgets = function () {
		my.super(...arguments);

		my.reportDisclaimerPrint = new ReportDisclaimerWidget({
			company: session.getCompany(),
			agency: session.agency,
			period: session.getPeriod(),
		});

		my.validationResultWidget = new ValidationResultWidget({
			collector: my.validationResultCollector,
			displayWarnings: false,
			supportInfo: session.getSupportInfo(),
		});

		my.displaySettings = displaySettingsWidget();

		my.displaySettingsPicker = popoverActionButtonWidget({
			label: _("Display settings"),
			popoverHeader: _("Display settings"),
			popoverContent: my.displaySettings,
			popoverAction: my.displaySettings.updateDisplaySettings,
			dataTestName: "display-settings",
			dataHelpName: "display-settings",
		});

		my.displaySettings.displaySettingsUpdated.register(() => {
			my.updateReport();
		});

		my.comparingPeriodDropdown = new ComparingPeriodDropdownWidget({
			years: session.getCompany().getActiveYears(),
			onChange: () => {
				my.updateReport();
			},
		});

		my.dimensionPicker = multipleDimensionsPickerWidget({
			dimensionTree: session.getDimensionTree(),
		});

		my.dimensionPicker.onDimensionsSelected.register(() => {
			my.updateReport();
		});

		my.columnsSettings = columnsSettingsWidget({
			settings: my.itemsSettings,
		});

		my.columnsSettings.settingsUpdated.register(() => {
			my.updateReport();
		});

		my.columnsSettingsPicker = popoverActionButtonWidget({
			label: _("Column settings"),
			popoverContent: my.columnsSettings,
			popoverAction: my.columnsSettings.updateSettings,
		});

		my.rowsSettings = rowsSettingsWidget({ settings: my.itemsSettings });

		my.rowsSettings.settingsUpdated.register(() => {
			my.updateReport();
		});

		my.rowsSettingsPicker = popoverActionButtonWidget({
			label: _("Row settings"),
			popoverContent: my.rowsSettings,
			popoverAction: my.rowsSettings.updateSettings,
		});

		my.rowVisibilityPicker = popoverActionButtonWidget({
			dataTestName: "optional-rows",
			label: _("Optional rows"),
			popoverHeader: _("Select optional rows"),
			popoverContent: (html) => my.renderOptionalRowsPickerOn(html),
			popoverAction: () => my.updateReport(),
		});

		my.columnVisibilityPicker = popoverActionButtonWidget({
			label: _("Optional columns"),
			popoverHeader: _("Select optional columns"),
			popoverContent: (html) => my.renderOptionalColumnsPickerOn(html),
			popoverAction: () => my.updateReport(),
		});

		my.reportViewer = new ReportViewerWidget({
			getCellContentClickedCallback: my.getCellContentClickedCallback,
			onAccountStatementRedirectionRequest: my.redirectToAccountStatement,
		});
	};

	that.hasPeriodPicker = function () {
		return true;
	};

	that.hasActionsMenu = function () {
		return true;
	};

	that.actions = function () {
		const editingActions = [];
		if (
			session
				.getCurrentUser()
				.isAdminUser(session.getCompany().getLegacyId())
		) {
			editingActions.push(my.editActionsRenderer());
		}

		const viewingActions = [
			(html) => {
				if (
					!my.getReportTemplate() ||
					!my.getReportTemplate().showPeriodComparisonPicker()
				) {
					return;
				}
				html.render(my.comparingPeriodRenderer());
			},
			my.dimensionPickerRenderer(),
			my.settingsActionsRenderer(),
			my.exportActionsRenderer(),
		];

		return editingActions.concat(viewingActions);
	};

	that.periodChanged = function () {
		my.updateDimensionTree();
		my.comparingPeriodDropdown.setCurrentPeriod(session.getPeriod());
		my.updateReport();
	};

	that.show = async function (reportTemplateId) {
		that.showUpdating();

		my.dimensionPicker.clearSelection();
		my.comparingPeriodDropdown.setCurrentPeriod(session.getPeriod());

		await Promise.all([
			my.reportService.fetchReportTemplate(reportTemplateId),
			my.reportService.fetchChartOfAccountsAndKeyFigures(),
		]);

		my.displaySettings.setDisplaySettings(
			my.getReportTemplate().getDisplaySettings(),
		);

		my.updateReport();
	};

	that.renderContentOn = function (html) {
		html.div({ klass: "print-only" }, my.reportDisclaimerPrint);

		that.super(...arguments);
	};

	my.renderContentOn = function (html) {
		if (!my.getReport()) {
			return;
		}

		my.renderAgencyLogoOn(html);
		html.render(my.validationResultWidget);
		my.renderDimensionFilterOn(html);
		html.render(my.reportViewer);
	};

	my.renderAgencyLogoOn = function (html) {
		const agencyHasLogo = theme.agencyPrintLogo;
		const shouldPrintLogo = my.getReport().shouldPrintLogo();

		if (agencyHasLogo && shouldPrintLogo) {
			html.div(
				{ klass: "report-agency-logo print-only" },
				html.img({ src: theme.agencyPrintLogo }),
			);
		}
	};

	my.renderDimensionFilterOn = function (html) {
		html.render(
			new InlineDimensionLabelsWidget({
				prefix: _("Report filtered on"),
				dimensionTree: session.getDimensionTree(),
				dimensionSpecification:
					my.dimensionPicker.getDimensionSpecification(),
			}),
		);
	};

	my.comparingPeriodRenderer = function () {
		return my.rightMenuSection(_("Compare period"), (html) => {
			html.div(
				{ "data-test": "compare-period" },
				my.comparingPeriodDropdown,
			);
		});
	};

	my.dimensionPickerRenderer = function () {
		return my.rightMenuSection(_("Filter projects"), (html) => {
			html.render(my.dimensionPicker);
		});
	};

	my.settingsActionsRenderer = function () {
		return my.rightMenuSection(
			_("Report settings"),
			my.displaySettingsPicker,
			my.showAccountsRenderer(),
			(html) => {
				if (my.columnsSettings.isEnabled()) {
					html.render(my.columnsSettingsPicker);
				}
				if (my.rowsSettings.isEnabled()) {
					html.render(my.rowsSettingsPicker);
				}
				if (
					my.getReportTemplate() &&
					my.getReportTemplate().hasOptionalRows()
				) {
					html.render(my.rowVisibilityPicker);
				}
				if (
					my.getReportTemplate() &&
					my.getReportTemplate().hasOptionalColumns()
				) {
					html.render(my.columnVisibilityPicker);
				}
			},
		);
	};

	my.showAccountsRenderer = function () {
		return (html) => {
			if (!my.getReportTemplate()) {
				return;
			}

			const button = new SwitchButton({
				state: my
					.getReportTemplate()
					.getDisplaySettings()
					.getAccountVisibility()
					.showsSomeAccounts(),
			});

			button.stateChanged.register((state) => {
				const factory = accountRowVisibilityFactory;

				my.setAccountsVisibility(
					state
						? factory.hideNoDataAccountsVisibility()
						: factory.hideAccountsVisibility(),
				);
			});

			html.div(
				{ klass: "right-menu-block" },
				html.span({ klass: "switch-label" }, _("Show accounts")),
				button,
			);
		};
	};

	my.exportActionsRenderer = function () {
		const reportExporterPicker = makeReportExporterPicker({
			report: my.getReport(),
			company: session.getCompany(),
			reportDisclaimerTemplateVariables:
				my.reportDisclaimerTemplateVariables(),
			selectedDimensionsLabel: my.selectedDimensionsLabelWithPrefix(),
		});

		return my.rightMenuSection(
			_("Export"),
			reportExporterPicker,
			my.renderPrintActionOn,
		);
	};

	my.renderPrintActionOn = function (html) {
		html.button(
			{
				klass: "btn btn-default btn-block",
				"data-help": "print-button",
				click: () => window.print(),
			},
			_("Print"),
		);
	};

	my.editActionsRenderer = function () {
		const customReportsFeature = optionsBag.fromId("custom-reports");

		return customReportsFeature.renderWhenActive(
			my.rightMenuSection("", (html) => {
				html.a(
					{
						klass: "btn btn-default btn-block btn-info",
						"data-test": "edit-report",
					},
					_("Edit report"),
				).click(() => {
					my.redirectTo(
						`report-editor/${my.getReportTemplate().getId()}`,
						{ reset: true },
					);
				});
			}),
		);
	};

	my.renderOptionalRowsPickerOn = function (html) {
		html.div(
			{ klass: "setting-section" },
			my.optionalItemsPicker(my.getReportTemplate().getAllOptionalRows()),
		);
	};

	my.renderOptionalColumnsPickerOn = function (html) {
		html.div(
			{ klass: "setting-section" },
			my.optionalItemsPicker(my.getReportTemplate().getOptionalColumns()),
		);
	};

	my.optionalItemsPicker = function (items) {
		return (html) => {
			html.render(items.map(my.optionalItemSettingRenderer));
		};
	};

	my.optionalItemSettingRenderer = function (item) {
		return (html) => {
			const button = new SwitchButton({
				state: item.isVisible(),
			});

			button.stateChanged.register((state) => item.setVisible(state));

			html.div({ klass: "setting-block" }, item.getTitle(), button);
		};
	};

	my.getReport = function () {
		return my.reportService.getReport();
	};

	my.getReportTemplate = function () {
		return my.reportService.getReportTemplate();
	};

	my.selectedDimensionsLabelWithPrefix = function () {
		if (!my.dimensionPicker.hasSelectedDimensions()) {
			return undefined;
		}
		return _("Report filtered on {dimensions}", {
			dimensions: my.selectedDimensionsLabel(),
		});
	};

	my.selectedDimensionsLabel = function () {
		return my.dimensionPicker.selectionLabel();
	};

	my.setAccountsVisibility = function (accountsVisilibity) {
		my.getReportTemplate()
			.getDisplaySettings()
			.setAccountVisibility(accountsVisilibity);

		my.displaySettings.refreshState();
		my.updateReport();
	};

	my.reportDisclaimerTemplateVariables = function () {
		return ReportDisclaimer.buildTemplateVariables({
			agency: session.agency,
			company: session.getCompany(),
			currentPeriod: session.getPeriod(),
		});
	};

	my.updateDimensionTree = function () {
		const currentDimensionTree = session.getDimensionTree();
		my.dimensionPicker.updateDimensionTree(currentDimensionTree);
	};

	my.updateReport = async function () {
		let repeatingPeriods = [];

		if (my.getReportTemplate().showPeriodComparisonPicker()) {
			const extraPeriods =
				my.comparingPeriodDropdown.getSelectedComparisonPeriods();
			repeatingPeriods = extraPeriods;
		}

		await my.reportService.updateReportAndData({
			repeatingPeriods,
			dimensionSpecification:
				my.dimensionPicker.getDimensionSpecification(),
			itemSettings: my.itemsSettings,
		});

		my.getReport().expandRowsUpToLevel(
			my.getReportTemplate().getDefaultExpansionLevel(),
		);

		my.columnsSettings.setReport(my.getReport());
		my.rowsSettings.setReport(my.getReport());
		my.reportViewer.setReport(my.getReport());
		my.reportViewer.update();
		my.updateActions();

		my.validationResultCollector.updateValidationResults({
			model: my.getReport(),
		});

		that.showContent();
	};

	my.willDetach = function () {
		[
			my.displaySettingsPicker,
			my.columnsSettingsPicker,
			my.rowsSettingsPicker,
			my.columnVisibilityPicker,
			my.rowVisibilityPicker,
			my.dimensionPicker,
		].forEach((picker) => picker.willDetach());
	};

	my.getCellContentClickedCallback = function ({ row, column }) {
		if (!row.isAccountRow() || !column.isPeriodColumn()) {
			return undefined;
		}

		return () =>
			my.redirectToAccountStatement({
				accountNumber: row.getAccount().getNumber(),
				period: () => column.getPeriod(),
			});
	};

	my.redirectToAccountStatement = function ({ accountNumber, period }) {
		window.open(
			my.router.linkTo("account-statement", {
				accountNumber,
				period,
				dimensionSpecification: my
					.getReport()
					.getDimensionSpecification()
					?.toString(),
			}),
		);
	};
});

export default reportViewerDocument;
